import { useState } from 'react';
import { createPremiumInvoiceLink } from '../lib/api';
import { useSession } from '../lib/session';
import { useT } from '../lib/i18n';
import { BackButton } from '../lib/BackButton';
import { Card, CardHint, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import { MainButton } from '../lib/MainButton';
import { getWebApp, hapticError, hapticTap } from '../lib/telegram';

export function PremiumPage(): JSX.Element {
  const { me, refresh } = useSession();
  const { t } = useT();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const purchase = async (): Promise<void> => {
    setError(null);
    setLoading(true);
    try {
      const { url } = await createPremiumInvoiceLink();
      const tg = getWebApp();
      if (tg?.openTelegramLink) tg.openTelegramLink(url);
      else window.open(url, '_blank');
      hapticTap();
      setTimeout(() => void refresh(), 4000);
    } catch (e) {
      hapticError();
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col px-4 py-6">
      <BackButton />
      <header className="mb-4 text-center">
        <div className="mx-auto mb-2 inline-flex items-center justify-center rounded-full bg-brand-navy px-3 py-1 text-xs font-semibold text-brand-cream">
          {t('premium.tag')}
        </div>
        <h1 className="text-2xl font-bold text-tg-text">{t('premium.title')}</h1>
        <p className="text-sm text-tg-hint">{t('premium.subtitle')}</p>
        <p className="mt-3 text-3xl font-bold text-brand-navy">
          {t('premium.price.value')}{' '}
          <span className="text-sm font-medium text-tg-hint">{t('premium.price.cadence')}</span>
        </p>
        {me?.isPremium && (
          <div className="mt-2 flex justify-center">
            <Badge tone="success">{t('premium.active')}</Badge>
          </div>
        )}
      </header>

      <Card className="space-y-2">
        <CardTitle>{t('premium.features.title')}</CardTitle>
        <ul className="space-y-2 text-sm text-tg-text">
          <li>· {t('premium.features.l1')}</li>
          <li>· {t('premium.features.l2')}</li>
          <li>· {t('premium.features.l3')}</li>
          <li>· {t('premium.features.l4')}</li>
        </ul>
        <CardHint>{t('premium.disclaimer')}</CardHint>
      </Card>

      {error && (
        <p className="mt-3 rounded-card bg-destructive/10 p-3 text-sm text-destructive">{error}</p>
      )}

      <a
        href="https://triardor.com/en/"
        target="_blank"
        rel="noreferrer"
        className="mt-6 block text-center text-xs text-tg-hint underline"
      >
        {t('common.madeByTriardor')}
      </a>

      <MainButton
        text={me?.isPremium ? t('premium.cta.renew') : t('premium.cta.buy')}
        loading={loading}
        onClick={purchase}
      />
    </div>
  );
}
